import { http } from '@pendo/http';
import get from 'lodash/get';
import {
    FIELDS_WITH_APP_ID_SUPPORT,
    parseEventMetadataUniqueValues,
} from '@/stores/helpers/metadata.helper';

export function fetchVisitorMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/visitor/schema?cardinality=${cardinality}`);
}

export function fetchAccountMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/account/schema?cardinality=${cardinality}`);
}

export function fetchParentAccountMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/parentAccount/schema?cardinality=${cardinality}`);
}

export function fetchEventMetadata ({ cardinality = false } = {}) {
    return http.get(`/api/s/_SID_/metadata/event/schema?cardinality=${cardinality}`).then((res) => {
        if (get(res, 'data.event', null)) {
            res.data.event = parseEventMetadataUniqueValues(res.data.event);
        }

        return res;
    });
}

export function fetchObjectMetadata () {
    return http.get('/api/s/_SID_/metadata/eventPropertyMetadata/schema');
}

export function retrieveMetadataByField (kind, group, fieldName, abortSignal) {
    return http.get(`/api/s/_SID_/metadata/${kind}/${group}/schema/${fieldName}`, { signal: abortSignal });
}

export function getSchemaForGroup ({ kind, group }) {
    return http.get(`/api/s/_SID_/metadata/${kind}/${group}/schema`);
}

export function getSchemaForKind (kind) {
    return http.get(`/api/s/_SID_/metadata/${kind}/schema`);
}

export function promoteSchema ({ kind, group, field }, promote) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/promotion`, promote);
}

export function updateSchema ({ kind, group, field }, data) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}`, data);
}

export function removeSchema ({ kind, group, field }) {
    return http.delete(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}`);
}

export function updateRules ({ kind, group, field }, body) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/eventurlrules`, body);
}

export function setSkipList ({ kind, group, field }, body) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/skiplist`, body);
}

export function normalizeUrl (url) {
    // if the URL already contains encoded characters, we need to decode them first so they
    // don't get encoded again below. Malformed percent-encoding (e.g. bare `%`) throws
    // URIError; in that case the URL wasn't encoded so we pass it through.
    let decoded;
    try {
        decoded = decodeURIComponent(url);
    } catch {
        decoded = url;
    }

    return http.post('/api/s/_SID_/helper/urlnorm', { url: encodeURI(decoded) });
}

export function parseUrlRule (rule) {
    return http.post('/api/s/_SID_/helper/urlpatternparse', { urlPattern: rule });
}

// you MUST pass `{}` as the body here. http strips the content-type header when
// posting with an empty body. failure to pass a body will result in 415 from pendo BE
export function updateViaEnabled ({ kind, group, field }) {
    return http.post(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/viaenabled`, {});
}

export function setObjectProperty ({ kind, group, field }, body) {
    return http.put(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/objectproperty`, body);
}

// Sets (or clears, with nameMetadata === '') the metadata field that carries
// the human-readable display name for values of the given object property.
// The URL identifies the original event-property field (which carries the
// nameMetadata slot on its FieldSchema); the body's `nameMetadata` names a
// field under `eventPropertyMetadata/<field>`. Backend gating: admin-only.
export function setObjectPropertyNameMetadata ({ kind, group, field }, { nameMetadata }) {
    return http.put(
        `/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/namemetadata`,
        { nameMetadata },
    );
}

export function queryMetadataValues (kind, group, field, params = {}) {
    if (params.app === '*' || group !== 'auto' || !FIELDS_WITH_APP_ID_SUPPORT.includes(field)) {
        delete params.app;
    }

    return http.get(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/values`, {
        params,
    });
}

export function queryExternalMetadataValues ({ kind, externalGroup, field }) {
    return http
        .get(`/api/s/_SID_/metadata/${kind}/${externalGroup}/schema/${field}/values/external`)
        .then((res) => res.data)
        .catch((err) => {
            throw err;
        });
}

export function generateMetadataDescription ({ kind, group, field, displayName, type, sample, description }) {
    return http.post(`/api/s/_SID_/metadata/${kind}/${group}/schema/${field}/generatedescription`, {
        DisplayName: displayName,
        Type: type,
        sample,
        description,
    });
}
